import React, { useEffect, useMemo, useState } from "react";
import Body from "../../layouts/Body";
import "../../assets/css/MentorMenteeMapping.css";

import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  CircularProgress,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Autocomplete,
  Checkbox,
  IconButton,
  Divider,
} from "@mui/material";

import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AddIcon from "@mui/icons-material/Add";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";

import MUIDataTable from "mui-datatables";

import {
  createFscFacilitatorMapping,
  getFscFacilitators,
  createUserCenterRoleMap,
  getActiveCentersByModel,
  getFacilitatorsForFscByCenter,
  getAvailableFacilitatorsForFscMapping,
  deactivateFscFacilitatorMapping,
} from "../../utils/services";

import { useUserContext } from "../../context/UserContext";

const FSS_CONFIG = {
  defaultProgramId: 13,
};

const PROGRAM_MAP = {
  6: "Odisha ITI",
  9: "Entrepreneurship",
  13: "Facilitator Support System",
};

const checkboxIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkboxCheckedIcon = <CheckBoxIcon fontSize="small" />;

const MentorMenteeMapping = () => {
  const { user } = useUserContext();

  const [fscList, setFscList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");

  const [selectedFsc, setSelectedFsc] = useState(null);
  const [openFacilitatorDialog, setOpenFacilitatorDialog] = useState(false);
  const [deletingMapId, setDeletingMapId] = useState(null);
  const [selectedToDelete, setSelectedToDelete] = useState([]);

  const [openAddFscDialog, setOpenAddFscDialog] = useState(false);
  const [addFscModelList, setAddFscModelList] = useState([]);
  const [addFscCenterList, setAddFscCenterList] = useState([]);
  const [selectedAddFscModel, setSelectedAddFscModel] = useState("");
  const [selectedAddFscCenter, setSelectedAddFscCenter] = useState("");
  const [addFscFacilitatorList, setAddFscFacilitatorList] = useState([]);
  const [selectedAddFscFacilitator, setSelectedAddFscFacilitator] = useState("");
  const [addFscModelLoading, setAddFscModelLoading] = useState(false);
  const [addFscFacilitatorLoading, setAddFscFacilitatorLoading] = useState(false);
  const [addFscLoading, setAddFscLoading] = useState(false);

  const [openAddFacilitatorDialog, setOpenAddFacilitatorDialog] = useState(false);
  const [selectedFscForAdd, setSelectedFscForAdd] = useState(null);

  const [addModelList, setAddModelList] = useState([]);
  const [addCenterList, setAddCenterList] = useState([]);
  const [selectedAddModel, setSelectedAddModel] = useState("");
  const [selectedAddCenter, setSelectedAddCenter] = useState("");
  const [selectedAddProgram, setSelectedAddProgram] = useState("");
  const [addModelLoading, setAddModelLoading] = useState(false);
  const [addCenterLoading, setAddCenterLoading] = useState(false);
  const [addFacilitatorList, setAddFacilitatorList] = useState([]);

  const [selectedAddFacilitators, setSelectedAddFacilitators] = useState([]);
  const [addFacilitatorLoading, setAddFacilitatorLoading] = useState(false);
  const [assignFacilitatorLoading, setAssignFacilitatorLoading] = useState(false);

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ").filter(Boolean);
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const fetchFscData = async () => {
    try {
      setLoading(true);
      setError("");

      const token = user.token;
      const result = await getFscFacilitators(token);

      if (result?.status && result.status !== 200) {
        throw new Error(result?.message || "Failed to fetch FSC data.");
      }

      const data = result?.data || [];

      // FSC and Program combination base chesukoni group chestham
      const groupedData = Object.values(
        data.reduce((acc, item) => {
          const fscId = item.userId;
          const progId = item.programId || item.program_id || item.programID || "-";
          // FSC ID + Program ID combo key
          const comboKey = `${fscId}_${progId}`;

          if (!acc[comboKey]) {
            acc[comboKey] = {
              comboKey,
              userId: item.userId,
              userName: item.userName,
              fscEmail: item.fscEmail,
              primaryDomain: item.primaryDomain,
              specialization: item.specialization,
              programId: progId,
              facilitators: [],
            };
          }

          if (item.menteeId || item.facilitatorEmail) {
            const alreadyExists = acc[comboKey].facilitators.some(
              (facilitator) =>
                Number(facilitator.menteeId) === Number(item.menteeId) &&
                Number(facilitator.menteeMapId) === Number(item.menteeMapId)
            );

            if (!alreadyExists) {
              acc[comboKey].facilitators.push({
                menteeId: item.menteeId,
                menteeMapId: item.menteeMapId,
                email: item.facilitatorEmail,
                programId: progId,
              });
            }
          }

          return acc;
        }, {})
      );

      setFscList(groupedData);

    if (selectedFsc) {
  const updatedFsc = groupedData.find((f) => f.comboKey === selectedFsc.comboKey);
  if (updatedFsc) {
    setSelectedFsc(updatedFsc);
  }
}
    } catch (err) {
      console.error("Error fetching FSC data:", err);
      setError(err?.message || "Unable to fetch FSC and facilitator details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.token) {
      fetchFscData();
    }
  }, [user?.token]);

  const handleRemoveFacilitators = async (mapIdsToRemove) => {
    if (!mapIdsToRemove || mapIdsToRemove.length === 0) {
      alert("Please select at least one facilitator to remove.");
      return;
    }

    if (!window.confirm(`Are you sure you want to remove ${mapIdsToRemove.length} facilitator(s)?`)) {
      return;
    }

    try {
      setDeletingMapId(mapIdsToRemove.length === 1 ? mapIdsToRemove[0] : "bulk");
      const token = user.token;

      const deletePromises = mapIdsToRemove.map((mapId) =>
        deactivateFscFacilitatorMapping(token, mapId)
      );

      const results = await Promise.all(deletePromises);

      const failedRequests = results.filter(
        (result) => result?.status && result.status !== 200 && result.status !== "200"
      );

      if (failedRequests.length > 0) {
        throw new Error("Some facilitators could not be removed.");
      }

      alert(`${mapIdsToRemove.length} facilitator(s) removed successfully.`);

      setSelectedFsc((prevFsc) => ({
        ...prevFsc,
        facilitators: (prevFsc?.facilitators || []).filter(
          (f) => !mapIdsToRemove.includes(f.menteeMapId || f.id)
        ),
      }));

      setSelectedToDelete((prev) =>
        prev.filter((id) => !mapIdsToRemove.includes(id))
      );

      await fetchFscData();
    } catch (err) {
      console.error("Error removing facilitator(s):", err);
      alert(err?.message || "Failed to remove facilitator(s).");
    } finally {
      setDeletingMapId(null);
    }
  };

  const handleToggleSelectToDelete = (mapId) => {
    setSelectedToDelete((prev) =>
      prev.includes(mapId)
        ? prev.filter((id) => id !== mapId)
        : [...prev, mapId]
    );
  };

  const handleSelectAllToDelete = () => {
    const allMapIds = selectedFsc?.facilitators?.map((f) => f.menteeMapId || f.id) || [];
    if (selectedToDelete.length === allMapIds.length) {
      setSelectedToDelete([]);
    } else {
      setSelectedToDelete(allMapIds);
    }
  };

  const fetchModelsAndCentersForAddFsc = async () => {
    try {
      setAddFscModelLoading(true);
      const token = user.token;
      const result = await getActiveCentersByModel(token);
      const data = Array.isArray(result) ? result : result?.data || [];

      setAddFscCenterList(data);

      const uniqueModels = Array.from(
        new Map(
          data
            .filter(
              (item) => item?.newModelId !== null && item?.newModelId !== undefined
            )
            .map((item) => [
              item.newModelId,
              {
                id: item.newModelId,
                name:
                  item.longCode ||
                  item.modelName ||
                  item.model ||
                  `Model ${item.newModelId}`,
              },
            ])
        ).values()
      );

      setAddFscModelList(uniqueModels);
    } catch (err) {
      console.error("Error fetching Add FSC models and centers:", err);
      setAddFscModelList([]);
      setAddFscCenterList([]);
      alert(err?.message || "Unable to fetch models and centers.");
    } finally {
      setAddFscModelLoading(false);
    }
  };

  const handleAddFscModelChange = (event) => {
    const modelId = event.target.value;
    setSelectedAddFscModel(modelId);
    setSelectedAddFscCenter("");
    setSelectedAddFscFacilitator("");
    setAddFscFacilitatorList([]);
  };

  const filteredAddFscCenters = useMemo(() => {
    if (!selectedAddFscModel) return [];
    return addFscCenterList.filter(
      (item) => String(item?.newModelId) === String(selectedAddFscModel)
    );
  }, [addFscCenterList, selectedAddFscModel]);

  const handleAddFscCenterChange = async (event) => {
    const centerId = event.target.value;
    setSelectedAddFscCenter(centerId);
    setSelectedAddFscFacilitator("");
    setAddFscFacilitatorList([]);

    if (!centerId) return;

    try {
      setAddFscFacilitatorLoading(true);
      const token = user.token;
      const result = await getFacilitatorsForFscByCenter(token, centerId);
      const data = Array.isArray(result) ? result : result?.data || [];
      setAddFscFacilitatorList(data);
    } catch (err) {
      console.error("Error fetching Add FSC facilitators:", err);
      setAddFscFacilitatorList([]);
      alert(err?.message || "Unable to fetch facilitators.");
    } finally {
      setAddFscFacilitatorLoading(false);
    }
  };

  const handleOpenAddFsc = async () => {
    setSelectedAddFscModel("");
    setSelectedAddFscCenter("");
    setSelectedAddFscFacilitator("");
    setAddFscModelList([]);
    setAddFscCenterList([]);
    setAddFscFacilitatorList([]);
    setOpenAddFscDialog(true);
    await fetchModelsAndCentersForAddFsc();
  };

  const handleCloseAddFsc = () => {
    if (addFscLoading) return;
    setOpenAddFscDialog(false);
    setSelectedAddFscModel("");
    setSelectedAddFscCenter("");
    setSelectedAddFscFacilitator("");
    setAddFscModelList([]);
    setAddFscCenterList([]);
    setAddFscFacilitatorList([]);
  };

  const handleAddFsc = async () => {
    if (!selectedAddFscModel || !selectedAddFscCenter || !selectedAddFscFacilitator) {
      alert("Please select Model, Center and Facilitator.");
      return;
    }

    try {
      setAddFscLoading(true);
      const token = user.token;

      const payload = {
        userId: Number(selectedAddFscFacilitator),
        centerId: 3049,
        roleMapId: 65,
        isDefault: "N",
        isActive: "Y",
        programId: 13,
        createdBy: user?.id ? Number(user.id) : null,
        updatedBy: user?.id ? Number(user.id) : null,
      };

      const result = await createUserCenterRoleMap(token, payload);

      const isSuccess =
        result?.status === 200 ||
        result?.status === 201 ||
        result?.status === "success" ||
        result?.statusCode === 200 ||
        result?.statusCode === 201 ||
        (result && !result.appError && result.status !== "faliure");

      if (!isSuccess) {
        const errorMsg =
          result?.appError?.[0]?.errorMessage ||
          result?.message ||
          "Failed to create FSC.";
        throw new Error(errorMsg);
      }

      alert("FSC created successfully.");
      handleCloseAddFsc();
      await fetchFscData();
    } catch (err) {
      console.error("Error creating FSC:", err);
      alert(err?.message || "Failed to create FSC.");
    } finally {
      setAddFscLoading(false);
    }
  };

  const handleOpenFacilitators = (fsc) => {
    setSelectedFsc(fsc);
    setSelectedToDelete([]);
    setOpenFacilitatorDialog(true);
  };

  const handleCloseFacilitators = () => {
    setOpenFacilitatorDialog(false);
    setSelectedFsc(null);
    setSelectedToDelete([]);
  };

  const fetchModelsAndCentersForAdd = async () => {
    try {
      setAddModelLoading(true);
      const token = user.token;
      const result = await getActiveCentersByModel(token);
      const data = Array.isArray(result) ? result : result?.data || [];

      setAddCenterList(data);

      const uniqueModels = Array.from(
        new Map(
          data
            .filter(
              (item) => item?.newModelId !== null && item?.newModelId !== undefined
            )
            .map((item) => [
              item.newModelId,
              {
                id: item.newModelId,
                name:
                  item.longCode ||
                  item.modelName ||
                  item.model ||
                  `Model ${item.newModelId}`,
              },
            ])
        ).values()
      );

      setAddModelList(uniqueModels);
    } catch (err) {
      console.error("Error fetching models and centers:", err);
      setAddModelList([]);
      setAddCenterList([]);
      alert(err?.message || "Unable to fetch models and centers.");
    } finally {
      setAddModelLoading(false);
    }
  };

  const handleAddModelChange = (event) => {
    const modelId = event.target.value;
    setSelectedAddModel(modelId);
    setSelectedAddCenter("");
    setSelectedAddFacilitators([]);
    setSelectedAddProgram("");
    setAddFacilitatorList([]);
  };

  const filteredAddCenters = useMemo(() => {
    if (!selectedAddModel) return [];
    return addCenterList.filter(
      (item) => String(item?.newModelId) === String(selectedAddModel)
    );
  }, [addCenterList, selectedAddModel]);

  const fetchFacilitatorsForSelectedCenter = async (centerId) => {
    try {
      setAddFacilitatorLoading(true);
      setAddFacilitatorList([]);
      const token = user.token;

      const result = await getAvailableFacilitatorsForFscMapping(token, centerId);
      const data = Array.isArray(result) ? result : result?.data || [];

      const assignedIds = (selectedFscForAdd?.facilitators || []).map(
        (facilitator) => Number(facilitator.menteeId)
      );

      const currentFscId = Number(selectedFscForAdd?.userId);

      const filteredData = data.filter((facilitator) => {
        const facId = Number(facilitator.id);
        return !assignedIds.includes(facId) && facId !== currentFscId;
      });

      setAddFacilitatorList(filteredData);
    } catch (err) {
      console.error("Error fetching facilitators by center:", err);
      setAddFacilitatorList([]);
      alert(err?.message || "Unable to fetch facilitators.");
    } finally {
      setAddFacilitatorLoading(false);
    }
  };

  const handleAddCenterChange = async (event) => {
    const centerId = event.target.value;
    setSelectedAddCenter(centerId);
    setSelectedAddFacilitators([]);
    setSelectedAddProgram("");
    setAddFacilitatorList([]);

    if (!centerId) return;
    await fetchFacilitatorsForSelectedCenter(centerId);
  };

  const handleOpenAddFacilitator = async (fsc) => {
    setSelectedFscForAdd(fsc);
    setSelectedAddModel("");
    setSelectedAddCenter("");
    setSelectedAddFacilitators([]);
    setSelectedAddProgram("");
    setAddModelList([]);
    setAddCenterList([]);
    setAddFacilitatorList([]);
    setOpenAddFacilitatorDialog(true);
    await fetchModelsAndCentersForAdd();
  };

  const handleCloseAddFacilitator = () => {
    if (assignFacilitatorLoading) return;
    setOpenAddFacilitatorDialog(false);
    setSelectedFscForAdd(null);
    setSelectedAddModel("");
    setSelectedAddCenter("");
    setSelectedAddFacilitators([]);
    setSelectedAddProgram("");
    setAddModelList([]);
    setAddCenterList([]);
    setAddFacilitatorList([]);
  };

  const handleFacilitatorSelectionChange = (event, newValue) => {
    const selectedIds = newValue.map((facilitator) => Number(facilitator.id));
    setSelectedAddFacilitators(selectedIds);
  };

  const handleAssignFacilitator = async () => {
    if (!selectedFscForAdd || selectedAddFacilitators.length === 0 || !selectedAddProgram) {
      alert("Please select Program and at least one facilitator.");
      return;
    }

    try {
      setAssignFacilitatorLoading(true);
      const token = user.token;

      const selectedFacilitatorObjects = addFacilitatorList.filter((facilitator) =>
        selectedAddFacilitators.includes(Number(facilitator.id))
      );

      if (selectedFacilitatorObjects.length === 0) {
        alert("Please select at least one facilitator.");
        return;
      }

      const mappingRequests = selectedFacilitatorObjects.map((facilitator) => {
        const payload = {
          mentorId: Number(selectedFscForAdd.userId),
          menteeId: Number(facilitator.id),
          isActive: "Y",
          programId: Number(selectedAddProgram),
          createdBy: user?.id ? Number(user.id) : null,
          updatedBy: user?.id ? Number(user.id) : null,
        };

        return createFscFacilitatorMapping(token, payload);
      });

      const results = await Promise.all(mappingRequests);

      const failedRequests = results.filter(
        (result) => result?.status && result.status !== 200 && result.status !== 201
      );

      if (failedRequests.length > 0) {
        throw new Error("Some facilitators could not be assigned.");
      }

      alert(`${selectedFacilitatorObjects.length} facilitator(s) assigned successfully.`);
      handleCloseAddFacilitator();
      await fetchFscData();
    } catch (err) {
      console.error("Error assigning facilitators:", err);
      alert(err?.message || "Failed to assign facilitators.");
    } finally {
      setAssignFacilitatorLoading(false);
    }
  };

  const filteredFscList = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    if (!search) return fscList;

    return fscList.filter((fsc) => {
      const facilitatorEmails = (fsc.facilitators || [])
        .map((facilitator) => facilitator.email || "")
        .join(" ");

      const programName = PROGRAM_MAP[Number(fsc.programId)] || PROGRAM_MAP[fsc.programId] || `Program ${fsc.programId}`;

      return (
        (fsc.userName || "").toLowerCase().includes(search) ||
        (fsc.fscEmail || "").toLowerCase().includes(search) ||
        (fsc.primaryDomain || "").toLowerCase().includes(search) ||
        (fsc.specialization || "").toLowerCase().includes(search) ||
        facilitatorEmails.toLowerCase().includes(search) ||
        programName.toLowerCase().includes(search)
      );
    });
  }, [fscList, searchText]);

  const tableData = filteredFscList.map((fsc) => ({
    userName: fsc.userName || "-",
    email: fsc.fscEmail || "-",
    primaryDomain: fsc.primaryDomain || "-",
    specialization: fsc.specialization || "-",
    programId: fsc.programId || "-",
    assignedFacilitators: (fsc.facilitators || [])
      .map((facilitator) => facilitator.email || "")
      .filter(Boolean)
      .join(", "),
    facilitatorCount: (fsc.facilitators || []).length,
  }));

  const columns = [
    {
      name: "userName",
      label: "FSC Name",
      options: {
        sort: true,
        filter: false,
        customBodyRender: (value) => (
          <Stack className="fsc-table-user" direction="row" spacing={1.5} alignItems="center">
            <Avatar className="fsc-avatar">{getInitials(value)}</Avatar>
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {value}
              </Typography>
            </Box>
          </Stack>
        ),
      },
    },
    {
      name: "email",
      label: "FSC Email",
      options: {
        sort: true,
        filter: false,
        customBodyRender: (value) => (
          <Typography variant="body2" color="text.secondary">{value}</Typography>
        ),
      },
    },
    {
      name: "primaryDomain",
      label: " FSC primary Domain",
      options: { sort: true, filter: false },
    },
    {
      name: "specialization",
      label: "FSC Specialization",
      options: { sort: true, filter: false },
    },
    {
  name: "programId",
  label: "Program Name",
  options: {
    sort: true,
    filter: true,
    filterOptions: {
      names: Array.from(
        new Set(
          tableData.map((row) => {
            const val = row.programId;
            return (
              PROGRAM_MAP[Number(val)] ||
              PROGRAM_MAP[String(val)] ||
              (val && val !== "-" ? `Program ${val}` : "-")
            );
          })
        )
      ),
      logic: (value, filters) => {
        if (!filters || filters.length === 0) return false;

        const currentName =
          PROGRAM_MAP[Number(value)] ||
          PROGRAM_MAP[String(value)] ||
          (value && value !== "-" ? `Program ${value}` : "-");

        return !filters.includes(currentName);
      },
    },
    customBodyRender: (value) => {
      const programName =
        PROGRAM_MAP[Number(value)] ||
        PROGRAM_MAP[String(value)] ||
        (value && value !== "-" ? `Program ${value}` : "-");
      return <Typography variant="body2">{programName}</Typography>;
    },
  },
},
    {
      name: "assignedFacilitators",
      label: "Assigned Facilitators",
      options: {
        sort: true,
        filter: true,
        filterType: "dropdown",
        filterOptions: {
          names: ["Assigned", "Not Assigned"],
          logic: (value, filters) => {
            if (!filters || filters.length === 0) return false;
            const selectedFilter = filters[0];
            const hasAssigned = value && String(value).trim() !== "";
            if (selectedFilter === "Assigned") return !hasAssigned;
            if (selectedFilter === "Not Assigned") return hasAssigned;
            return false;
          },
        },
        customBodyRender: (value, tableMeta) => {
          const fsc = filteredFscList[tableMeta.rowIndex];
          const facilitators = fsc?.facilitators || [];

          if (facilitators.length === 0) {
            return <Chip label="Not Assigned" size="small" variant="outlined" color="error" sx={{ borderRadius: "6px" }} />;
          }

          const firstFacilitator = facilitators[0];
          const remainingCount = facilitators.length - 1;

          return (
            <Box className="assigned-facilitators">
              <Chip
                avatar={<Avatar>{getInitials(firstFacilitator.email)}</Avatar>}
                label={firstFacilitator.email || "Facilitator 1"}
                size="small"
                variant="outlined"
                onClick={(event) => {
                  event.stopPropagation();
                  handleOpenFacilitators(fsc);
                }}
                sx={{ cursor: "pointer", borderRadius: "6px" }}
              />

              {remainingCount > 0 && (
                <Chip
                  label={`+${remainingCount}`}
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleOpenFacilitators(fsc);
                  }}
                  className="more-facilitators-chip"
                  sx={{ cursor: "pointer", borderRadius: "6px" }}
                />
              )}
            </Box>
          );
        },
      },
    },
    {
      name: "facilitatorCount",
      label: "Facilitators Count",
      options: {
        sort: true,
        filter: false,
        customBodyRender: (value) => (
          <Typography variant="body2" fontWeight={600} textAlign="center">
            {value}
          </Typography>
        ),
      },
    },
    {
      name: "action",
      label: "Action",
      options: {
        sort: false,
        filter: false,
        searchable: false,
        download: false,
        print: false,
        customBodyRender: (value, tableMeta) => {
          const fsc = filteredFscList[tableMeta.rowIndex];

          return (
            <Button
              variant="contained"
              size="small"
              startIcon={<PersonAddAltOutlinedIcon />}
              onClick={() => handleOpenAddFacilitator(fsc)}
              className="add-facilitator-button"
            >
              Add Facilitator
            </Button>
          );
        },
      },
    },
  ];

  const options = {
    selectableRows: "none",
    search: true,
    download: true,
    print: false,
    viewColumns: true,
    filter: true,
    responsive: "simple",
    pagination: true,
    rowsPerPage: 10,
    rowsPerPageOptions: [5, 10, 25, 50, 100],
    searchText: searchText,
    onSearchChange: (text) => setSearchText(text || ""),

   onDownload: () => {
  const headers = [
    "FSC Name",
    "FSC Email",
    "Domain/YDM",
    "FSC Specialization",
    "Program Name",
    "Assigned Facilitator Emails",
    "Facilitators Count",
  ];

  const rows = filteredFscList.map((fsc) => {
    const facilitatorsList = (fsc.facilitators || [])
      .map((facilitator) => facilitator.email || "")
      .filter(Boolean);

    const facilitators =
      facilitatorsList.length > 0
        ? facilitatorsList
            .map((email, idx) =>
              idx === facilitatorsList.length - 1 ? email : `${email},`
            )
            .join("\n")
        : "Not Assigned";

    // BOTH String and Number formats ni handle chese fix:
    const rawProgId = fsc.programId;
    const programName =
      PROGRAM_MAP[Number(rawProgId)] ||
      PROGRAM_MAP[String(rawProgId)] ||
      (rawProgId && rawProgId !== "-" ? `Program ${rawProgId}` : "-");

    return [
      fsc.userName || "-",
      fsc.fscEmail || "-",
      fsc.primaryDomain || "-",
      fsc.specialization || "-",
      programName,
      facilitators,
      facilitatorsList.length,
    ];
  });

  const csvRows = [headers, ...rows];
  const csvContent = csvRows
    .map((row) =>
      row
        .map((value) => {
          const stringValue = String(value);
          return `"${stringValue.replace(/"/g, '""')}"`;
        })
        .join(",")
    )
    .join("\n");

  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "fsc-management.csv";
  link.click();
  URL.revokeObjectURL(url);

  return false;
},
  };

  // Dynamic unique FSC count calculate cheyataniki
  const totalUniqueFscs = new Set(fscList.map((fsc) => fsc.userId)).size;

  const unassignedFscCount = fscList.filter(
    (fsc) => !fsc.facilitators || fsc.facilitators.length === 0
  ).length;

  return (
    <Body>
      <Box className="fsc-management-page">
        <Box className="fsc-management-header">
          <Typography
            variant="h5"
            className="page-title text-centered"
            color="primary"
          >
            FSC's Management
          </Typography>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddFsc}
            className="add-fsc-button"
          >
            Create FSC
          </Button>
        </Box>

        <Box className="fsc-summary-grid">
          <Card className="summary-card glass-card-blue">
            <CardContent className="summary-card-content-wrapper">
              <Stack direction="row" spacing={2.5} alignItems="center">
                <Box className="avatar-wrapper blue-glow">
                  <Avatar className="summary-avatar avatar-blue">
                    <GroupsOutlinedIcon />
                  </Avatar>
                </Box>
                <Box>
                  <Typography className="summary-label">Total FSC's</Typography>
                  <Typography className="summary-value">{totalUniqueFscs}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card className="summary-card glass-card-orange">
            <CardContent className="summary-card-content-wrapper">
              <Stack direction="row" spacing={2.5} alignItems="center">
                <Box className="avatar-wrapper orange-glow">
                  <Avatar className="summary-avatar avatar-orange">
                    <PersonOutlineOutlinedIcon />
                  </Avatar>
                </Box>
                <Box>
                  <Typography className="summary-label">
                    FSCs without Facilitators
                  </Typography>
                  <Typography className="summary-value">
                    {unassignedFscCount}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        <Card elevation={0} sx={{ border: "1px solid #eaecf0", borderRadius: "12px" }}>
          {loading ? (
            <Box className="fsc-loading-container">
              <CircularProgress />
            </Box>
          ) : (
            <MUIDataTable
              title=""
              data={tableData}
              columns={columns}
              options={options}
            />
          )}
        </Card>

        <Dialog
          open={openFacilitatorDialog}
          onClose={handleCloseFacilitators}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: "12px" } }}
        >
          <DialogTitle className="dialog-header-box">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={700}>Assigned Facilitators</Typography>
              <IconButton size="small" onClick={handleCloseFacilitators}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 2.5 }}>
            <Box className="dialog-fsc-info-card" sx={{ mb: 2, display: "flex", justifyContent: "space-between", }}>
              <Typography variant="body2" color="text.secondary">
                FSC Name: <strong>{selectedFsc?.userName || "-"}</strong>
              </Typography>

              {selectedFsc?.facilitators?.length > 0 && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Checkbox
                    size="small"
                    checked={
                      selectedToDelete.length === selectedFsc.facilitators.length &&
                      selectedFsc.facilitators.length > 0
                    }
                    indeterminate={
                      selectedToDelete.length > 0 &&
                      selectedToDelete.length < selectedFsc.facilitators.length
                    }
                    onChange={handleSelectAllToDelete}
                  />
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Select All
                  </Typography>
                </Stack>
              )}
            </Box>

            <Stack spacing={1.5}>
              {selectedFsc?.facilitators?.length ? (
                selectedFsc.facilitators.map((facilitator, index) => {
                  const mapIdToDelete = facilitator.menteeMapId || facilitator.id;
                  const isChecked = selectedToDelete.includes(mapIdToDelete);
                  const isDeletingThis = deletingMapId === mapIdToDelete;

                  return (
                    <Card key={facilitator.menteeId || index} variant="outlined" sx={{ borderRadius: "8px" }}>
                      <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                        <Stack
                          direction="row"
                          spacing={2}
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Checkbox
                              size="small"
                              checked={isChecked}
                              onChange={() => handleToggleSelectToDelete(mapIdToDelete)}
                            />
                            <Avatar sx={{ width: 32, height: 32, fontSize: "12px" }}>
                              {getInitials(facilitator.email)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {facilitator.email || "Facilitator"}
                              </Typography>
                            </Box>
                          </Stack>

                          <Tooltip title="Remove Facilitator">
                            <IconButton
                              color="error"
                              size="small"
                              disabled={deletingMapId !== null}
                              onClick={() => handleRemoveFacilitators([mapIdToDelete])}
                            >
                              {isDeletingThis ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DeleteOutlineIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <Typography color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                  No facilitators assigned.
                </Typography>
              )}
            </Stack>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
            {selectedToDelete.length > 0 ? (
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => handleRemoveFacilitators(selectedToDelete)}
                disabled={deletingMapId !== null}
              >
                {deletingMapId === "bulk"
                  ? "Removing..."
                  : `Delete Selected (${selectedToDelete.length})`}
              </Button>
            ) : (
              <Box />
            )}

            <Button onClick={handleCloseFacilitators} variant="outlined" className="dialog-cancel-btn">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* ADD FACILITATOR DIALOG WITH PROGRAM SELECTION AFTER FACILITATOR */}
        <Dialog
          open={openAddFacilitatorDialog}
          onClose={handleCloseAddFacilitator}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: "12px" } }}
        >
          <DialogTitle className="dialog-header-box">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={700}>Add Facilitator</Typography>
              <IconButton size="small" onClick={handleCloseAddFacilitator}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 2.5 }}>
            <Box className="dialog-fsc-info-card" sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                FSC Name: <strong>{selectedFscForAdd?.userName || "-"}</strong>
              </Typography>
            </Box>

            {/* 1. SELECT MODEL */}
            <TextField
              select
              fullWidth
              size="small"
              label="Select Model"
              value={selectedAddModel}
              onChange={handleAddModelChange}
              disabled={addModelLoading || assignFacilitatorLoading}
              className="add-fsc-field"
            >
              {addModelLoading ? (
                <MenuItem disabled>Loading models...</MenuItem>
              ) : addModelList.length === 0 ? (
                <MenuItem disabled>No models available</MenuItem>
              ) : (
                addModelList.map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    {model.name}
                  </MenuItem>
                ))
              )}
            </TextField>

            {/* 2. SELECT CENTER */}
            <TextField
              select
              fullWidth
              size="small"
              label="Select Center"
              value={selectedAddCenter}
              onChange={handleAddCenterChange}
              disabled={
                !selectedAddModel ||
                addModelLoading ||
                addCenterLoading ||
                assignFacilitatorLoading
              }
              className="add-fsc-field"
            >
              {!selectedAddModel ? (
                <MenuItem disabled>Please select a model first</MenuItem>
              ) : filteredAddCenters.length === 0 ? (
                <MenuItem disabled>
                  No centers available for this model
                </MenuItem>
              ) : (
                filteredAddCenters.map((center) => (
                  <MenuItem key={center.id} value={center.id}>
                    {center.centerName ||
                      center.name ||
                      center.longCode ||
                      `Center ${center.id}`}
                  </MenuItem>
                ))
              )}
            </TextField>

            {/* 3. SELECT FACILITATOR */}
            <Autocomplete
              multiple
              fullWidth
              size="small"
              disableCloseOnSelect
              options={addFacilitatorList}
              value={addFacilitatorList.filter((facilitator) =>
                selectedAddFacilitators.includes(Number(facilitator.id))
              )}
              onChange={handleFacilitatorSelectionChange}
              getOptionLabel={(option) =>
                option.emailAddress ||
                option.email ||
                option.fscEmail ||
                option.userName ||
                `Facilitator ${option.id}`
              }
              isOptionEqualToValue={(option, value) =>
                Number(option.id) === Number(value.id)
              }
              disabled={
                !selectedAddCenter ||
                addFacilitatorLoading ||
                assignFacilitatorLoading
              }
              loading={addFacilitatorLoading}
              noOptionsText={
                !selectedAddCenter
                  ? "Please select a center first"
                  : "No facilitators available"
              }
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={checkboxIcon}
                    checkedIcon={checkboxCheckedIcon}
                    checked={selected}
                    sx={{ mr: 1 }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {option.emailAddress ||
                        option.email ||
                        option.fscEmail ||
                        option.userName ||
                        `Facilitator ${option.id}`}
                    </Typography>
                    {option.userName && (option.emailAddress || option.email) && (
                      <Typography variant="caption" color="text.secondary">
                        {option.userName}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderTags={(selectedOptions, getTagProps) =>
                selectedOptions.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={
                      option.emailAddress ||
                      option.email ||
                      option.fscEmail ||
                      option.userName ||
                      `Facilitator ${option.id}`
                    }
                    size="small"
                    variant="outlined"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Facilitators"
                  placeholder={
                    selectedAddFacilitators.length === 0
                      ? "Search and select facilitators"
                      : ""
                  }
                  className="add-fsc-field"
                />
              )}
            />

            {/* 4. SELECT PROGRAM */}
            <TextField
              select
              fullWidth
              size="small"
              label="Select Program"
              value={selectedAddProgram}
              onChange={(e) => setSelectedAddProgram(e.target.value)}
              disabled={selectedAddFacilitators.length === 0 || assignFacilitatorLoading}
              className="add-fsc-field"
            >
              {Object.entries(PROGRAM_MAP).map(([id, name]) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </TextField>

            {selectedAddFacilitators.length > 0 && (
              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Chip
                  label={`${selectedAddFacilitators.length} facilitator${
                    selectedAddFacilitators.length > 1 ? "s" : ""
                  } selected`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={handleCloseAddFacilitator}
              disabled={assignFacilitatorLoading}
              variant="outlined"
              className="dialog-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAssignFacilitator}
              disabled={
                !selectedAddModel ||
                !selectedAddCenter ||
                !selectedAddProgram ||
                selectedAddFacilitators.length === 0 ||
                addModelLoading ||
                addFacilitatorLoading ||
                assignFacilitatorLoading
              }
              className="dialog-submit-btn"
            >
              {assignFacilitatorLoading
                ? "Adding..."
                : selectedAddFacilitators.length > 0
                ? `Add ${selectedAddFacilitators.length} Facilitator${
                    selectedAddFacilitators.length > 1 ? "s" : ""
                  }`
                : "Add Facilitator"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* CREATE FSC DIALOG */}
        <Dialog
          open={openAddFscDialog}
          onClose={handleCloseAddFsc}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: "12px" } }}
        >
          <DialogTitle className="dialog-header-box">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={700}>Create FSC</Typography>
              <IconButton size="small" onClick={handleCloseAddFsc}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 2.5 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Select Model"
              value={selectedAddFscModel}
              onChange={handleAddFscModelChange}
              disabled={addFscModelLoading || addFscLoading}
              className="add-fsc-field"
            >
              {addFscModelLoading ? (
                <MenuItem disabled>Loading models...</MenuItem>
              ) : addFscModelList.length === 0 ? (
                <MenuItem disabled>No models available</MenuItem>
              ) : (
                addFscModelList.map((model) => (
                  <MenuItem key={model.id} value={model.id}>
                    {model.name}
                  </MenuItem>
                ))
              )}
            </TextField>

            <TextField
              select
              fullWidth
              size="small"
              label="Select Center"
              value={selectedAddFscCenter}
              onChange={handleAddFscCenterChange}
              disabled={
                !selectedAddFscModel ||
                addFscModelLoading ||
                addFscFacilitatorLoading ||
                addFscLoading
              }
              className="add-fsc-field"
            >
              {!selectedAddFscModel ? (
                <MenuItem disabled>Please select a model first</MenuItem>
              ) : filteredAddFscCenters.length === 0 ? (
                <MenuItem disabled>
                  No centers available for this model
                </MenuItem>
              ) : (
                filteredAddFscCenters.map((center) => (
                  <MenuItem key={center.id} value={center.id}>
                    {center.centerName ||
                      center.name ||
                      center.longCode ||
                      `Center ${center.id}`}
                  </MenuItem>
                ))
              )}
            </TextField>

            <TextField
              select
              fullWidth
              size="small"
              label="Select Facilitator"
              value={selectedAddFscFacilitator}
              onChange={(event) =>
                setSelectedAddFscFacilitator(event.target.value)
              }
              disabled={
                !selectedAddFscCenter ||
                addFscFacilitatorLoading ||
                addFscLoading
              }
              className="add-fsc-field"
            >
              {!selectedAddFscCenter ? (
                <MenuItem disabled>Please select a center first</MenuItem>
              ) : addFscFacilitatorLoading ? (
                <MenuItem disabled>Loading facilitators...</MenuItem>
              ) : addFscFacilitatorList.length === 0 ? (
                <MenuItem disabled>No facilitators available</MenuItem>
              ) : (
                addFscFacilitatorList.map((facilitator) => (
                  <MenuItem key={facilitator.id} value={facilitator.id}>
                    {facilitator.emailAddress ||
                      facilitator.email ||
                      facilitator.fscEmail ||
                      facilitator.userName ||
                      `Facilitator ${facilitator.id}`}
                  </MenuItem>
                ))
              )}
            </TextField>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleCloseAddFsc} disabled={addFscLoading} variant="outlined" className="dialog-cancel-btn">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddFsc}
              disabled={
                !selectedAddFscModel ||
                !selectedAddFscCenter ||
                !selectedAddFscFacilitator ||
                addFscModelLoading ||
                addFscFacilitatorLoading ||
                addFscLoading
              }
              className="dialog-submit-btn"
            >
              {addFscLoading ? "Creating..." : "Create FSC"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Body>
  );
};

export default MentorMenteeMapping;